/**
 * The Editing Room API module (Unit 4 build spec §B6, backend
 * api/editing_room.py). client.js attaches auth automatically and returns
 * the raw body; `unwrap` mirrors api/storyBoard.js's convention even though
 * this backend never wraps responses in a `data` envelope — it's a no-op
 * passthrough here, kept for consistency with the rest of the app's API
 * modules.
 */
import { api } from './client';

const unwrap = (res) => (res?.data?.data ?? res?.data ?? res);

export async function getWriterModels() {
  return unwrap(await api.get('/editing-room/models'));
}

export async function getCheckingStatus() {
  return unwrap(await api.get('/editing-room/checking-status'));
}

export async function getSceneContext(sceneId) {
  return unwrap(await api.get(`/editing-room/scenes/${sceneId}/context`));
}

export async function preflightScene(sceneId) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/preflight`, {}));
}

export async function answerPreflightQuestion(sceneId, { type, character, answer }) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/preflight/answer`, {
    type, character: character || null, answer,
  }));
}

export async function getPriceEstimate(sceneId, mode) {
  return unwrap(await api.get(`/editing-room/scenes/${sceneId}/price-estimate?mode=${encodeURIComponent(mode)}`));
}

export async function getGenerationStatus(sceneId) {
  return unwrap(await api.get(`/editing-room/scenes/${sceneId}/generation-status`));
}

export async function generateScene(sceneId, { model, mode, overrideDossierReason } = {}) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/generate`, {
    model: model || null,
    mode: mode || 'verified',
    override_dossier_reason: overrideDossierReason || null,
  }));
}

export async function regenerateScene(sceneId, { agreedFindingIds, overruledFindingIds, model, writerNote } = {}) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/regenerate`, {
    agreed_finding_ids: agreedFindingIds || [],
    overruled_finding_ids: overruledFindingIds || [],
    model: model || null,
    writer_note: writerNote || null,
  }));
}

export async function editScene(sceneId, draft) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/edit`, { draft }));
}

export async function recheckScene(sceneId) {
  return unwrap(await api.post(`/editing-room/scenes/${sceneId}/recheck`, {}));
}

function parseJsonbField(value) {
  return typeof value === 'string' ? JSON.parse(value) : value;
}

/**
 * asyncpg has no JSONB codec registered on this pool (backend's own
 * pipeline._decode_jsonb carries the same note) — fixes/beat_account/
 * overrides arrive here as JSON-encoded strings, not objects.
 */
export async function listSceneReceipts(sceneId) {
  const { receipts } = unwrap(await api.get(`/editing-room/scenes/${sceneId}/receipts`));
  return {
    receipts: receipts.map((r) => ({
      ...r,
      fixes: parseJsonbField(r.fixes),
      beat_account: parseJsonbField(r.beat_account),
      overrides: parseJsonbField(r.overrides),
    })),
  };
}

export async function confirmFact(factId) {
  return unwrap(await api.post(`/editing-room/facts/${factId}/confirm`, {}));
}

export async function rejectFact(factId) {
  return unwrap(await api.post(`/editing-room/facts/${factId}/reject`, {}));
}
